import { useState } from "react";
import { Plus } from "lucide-react";
import { Link } from "react-router";
import type { List } from "@guestbook/shared";
import { Button } from "@/shared/components/ui/Button";
import { Checkbox } from "@/shared/components/ui/Checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/Dialog";
import { Input } from "@/shared/components/ui/Input";
import { useContactsList } from "@/shared/lib/contacts";
import { useAddListMembers } from "@/shared/lib/lists";
import { useScopedHref } from "@/shared/lib/scope";
import { cn } from "@/shared/lib/utils";

const PAGE_LIMIT = 50;

interface AddExistingContactsDialogProps {
  list: List;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Picker shown from the contacts page "Add Contact" button when a list scope
 * is active. Lets the user multi-select contacts that aren't already in the
 * scoped list and add them in one shot. The "Or create a new contact" link
 * is the escape hatch into the existing /app/contacts/new form, which is
 * already scope-aware and auto-attaches the new contact to the active list.
 */
export function AddExistingContactsDialog({
  list,
  open,
  onOpenChange,
}: AddExistingContactsDialogProps) {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const href = useScopedHref();

  const candidates = useContactsList({
    excludeListId: list.id,
    search: search || undefined,
    limit: PAGE_LIMIT,
  });
  const items = candidates.data?.items ?? [];
  const add = useAddListMembers(list.id);

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function reset() {
    setSearch("");
    setSelectedIds(new Set());
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function handleSubmit() {
    const members = [...selectedIds].map((id) => ({ contactId: id }));
    add.mutate(
      { members },
      {
        onSuccess: () => handleOpenChange(false),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="2xl" className="max-h-[85vh] gap-5">
        <DialogHeader>
          <DialogTitle>Add contacts to {list.name}</DialogTitle>
          <DialogDescription>
            Pick contacts that aren't already in this list.
          </DialogDescription>
        </DialogHeader>

        <Input
          type="search"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search contacts"
        />

        <div className="max-h-[50vh] overflow-y-auto rounded-xl border border-border-muted glass-surface-subtle">
          {candidates.isLoading ? (
            <p className="p-4 type-body-base text-text-muted">Loading…</p>
          ) : candidates.isError ? (
            <p className="p-4 type-body-base text-danger-strong">
              Couldn't load contacts: {candidates.error.message}
            </p>
          ) : items.length === 0 ? (
            <p className="p-6 text-center type-body-base text-text-muted">
              {search
                ? "No matching contacts outside this list."
                : "Every contact is already in this list."}
            </p>
          ) : (
            <ul className="flex flex-col">
              {items.map((c) => {
                const selected = selectedIds.has(c.id);
                const name = c.name || "—";
                return (
                  <li key={c.id}>
                    <label
                      className={cn(
                        "flex cursor-pointer items-center gap-3 border-t border-border-muted px-4 py-2.5 first:border-t-0",
                        "transition-colors duration-150 hover:bg-surface-muted/40",
                        selected && "bg-surface-muted/30",
                      )}
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => toggleRow(c.id)}
                        aria-label={`Select ${name}`}
                      />
                      <span className="min-w-0 truncate type-body-base text-text">
                        {name}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {(candidates.data?.total ?? 0) > items.length ? (
          <p className="type-body-sm text-text-muted">
            More results — refine your search to narrow them down.
          </p>
        ) : null}

        {add.error ? (
          <p className="type-body-sm text-danger-strong">
            Couldn't add contacts: {add.error.message}
          </p>
        ) : null}

        <Button asChild variant="secondary" className="w-full">
          <Link to={href("/app/contacts/new")}>
            <Plus aria-hidden className="h-4 w-4" />
            Create a new contact
          </Link>
        </Button>

        <div className="flex flex-row-reverse gap-2">
          <Button
            onClick={handleSubmit}
            disabled={selectedIds.size === 0 || add.isPending}
          >
            {add.isPending
              ? "Adding…"
              : `Add ${selectedIds.size} ${
                  selectedIds.size === 1 ? "contact" : "contacts"
                }`}
          </Button>
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
